import React from "react";
import Button from "components/Button/Regular";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import { useNavigate } from "react-router-dom";
import Page from "components/Page/Page";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { getStaffList, StaffRow } from "services/lists.service";
import { deleteOrgMember } from "services/staff.service";
import vgToast from "utils/toast";

const StaffList = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { orgId } = useActiveOrg();
  const [staff, setStaff] = React.useState<StaffRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Client-side paging and sorting: the list endpoints return whole
  // collections with no paging parameters, so there is nothing to page
  // server-side against.
  const view = useTableView(staff);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0) {
      setStaff([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await getStaffList(token, orgId);
      if (apiError) {
        setError(apiError.message);
        setStaff([]);
        return;
      }
      setStaff(data);
    } catch (e) {
      setError((e as Error).message);
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Remove this staff member from the organisation?")) return;
    if (orgId <= 0) return;
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await deleteOrgMember(token, orgId, Number(id));
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    }
  };

  return (
    <Page
      title="Staff"
      subTitle="Manage the people who work for your provider."
      actions={
        <Button onClick={() => navigate("/app/staff/new-staff")}>
          <PlusIcon />
          <span>Add Staff Member</span>
        </Button>
      }
    >
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="mb-3"
      />

      <Table<{
        id: string;
        name: string;
        role: string;
      }>
        loading={loading}
        columns={{
          id: "Staff Id",
          name: "Staff Name",
          role: "Role",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        emptyMessage="No staff members yet. Add your first staff member to get started."
        handleViewItem={(id) => navigate(`/app/staff/${id}`)}
        handleRemoveItem={handleDelete}
      />
      {error && <div className="mt-3 text-sm text-red-500">{error}</div>}

      <div className="mt-4">
        <PaginationBar
          from={view.from}
          to={view.to}
          total={view.total}
          currentPage={view.page}
          totalPages={view.totalPages}
          onPrev={view.goPrev}
          onNext={view.goNext}
          onPageSelect={view.goToPage}
        />
      </div>
    </Page>
  );
};

export default StaffList;
